import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'wouter'
import { motion } from 'motion/react'
import { fetchMe, fetchDevices, fetchPrimaryLink, type Device } from '@/lib/api'
import { AppHeader } from '@/components/AppHeader'
import { Icon } from '@/components/Icon'
import { haptic, openHapp, getPlatform } from '@/lib/tg'
import cicadaWhite from '@/assets/cicada_white.png'
import cicadaBlack from '@/assets/cicada_black.png'

const PLAT_ICON: Record<string, 'device-mobile' | 'device-desktop' | 'device-laptop'> = {
  ios: 'device-mobile', android: 'device-mobile', macos: 'device-desktop',
  windows: 'device-laptop', linux: 'device-laptop', other: 'device-mobile',
}

export function Home() {
  const [, setLocation] = useLocation()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const { data: devices } = useQuery({ queryKey: ['devices'], queryFn: fetchDevices })

  const sub = me?.subscription
  const active = !!sub?.is_active
  const limit = me?.device_limit ?? sub?.device_limit ?? 3
  const used = me?.devices_used ?? (devices?.length ?? 0)
  const usedGb = sub?.traffic_used_gb ?? 0
  const limitGb = sub?.traffic_limit_gb ?? 2

  const { data: link, isFetching } = useQuery({
    queryKey: ['primaryLink'], queryFn: fetchPrimaryLink, enabled: active, staleTime: 5 * 60_000,
  })

  const plat = getPlatform()
  const others: Device[] = (devices ?? []).filter(d => d.id !== link?.device_id)

  const onConnect = () => {
    haptic('medium')
    if (!active) { setLocation('/plans'); return }
    if (link?.happ_link) openHapp(link.happ_link, link.sub_link)
  }

  const connectLabel = !active ? 'ОФОРМИТЬ ДОСТУП' : (isFetching && !link ? 'СОЕДИНЕНИЕ…' : 'ПОДКЛЮЧИТЬ')

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col min-h-0 overflow-hidden">

      <AppHeader />

      {/* cicada hero */}
      <div className="flex-1 min-h-[80px] flex items-center justify-center px-5 py-1">
        <img src={cicadaBlack} alt="" className="only-light max-h-full w-auto max-w-[215px] object-contain select-none pointer-events-none" />
        <img src={cicadaWhite} alt="" className="only-dark max-h-full w-auto max-w-[215px] object-contain select-none pointer-events-none" />
      </div>

      {/* main block */}
      <div className="px-5 shrink-0">
        <div className="flex items-end gap-2.5">
          <h1 className="display text-[44px] leading-[0.78]">CICADA<span style={{ color: 'var(--color-accent)' }}> 01</span></h1>
          <span className="num text-[10px] tracking-[0.18em] uppercase pb-1.5" style={{ color: 'var(--color-muted)' }}>узел · ru→eu</span>
        </div>
        <div className="rule w-11 mt-2.5 mb-3" />

        {/* stat chips */}
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          <div className="block p-2.5">
            <div className="label">устройства</div>
            <div className="num text-[15px] font-bold mt-1">{used} <span className="text-[11px]" style={{ color: 'var(--color-muted)' }}>/ {limit}</span></div>
          </div>
          <div className="block p-2.5">
            <div className="label">трафик сегодня</div>
            <div className="num text-[15px] font-bold mt-1" style={{ color: 'var(--color-green)' }}>{usedGb} <span className="text-[11px]" style={{ color: 'var(--color-muted)' }}>/ {limitGb} ГБ</span></div>
          </div>
        </div>

        {/* this-device connect */}
        <div className="block overflow-hidden">
          {active && (
            <div className="flex items-center justify-between px-3.5 pt-3 pb-2.5">
              <div className="flex items-center gap-2.5">
                <span className="size-[34px] rounded-[8px] flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
                  <Icon name={plat.icon} size={19} />
                </span>
                <div>
                  <div className="text-[15px] font-semibold">{plat.label}</div>
                  <div className="num text-[10.5px] mt-0.5" style={{ color: 'var(--color-muted)' }}>
                    {link ? `ключ #${link.device_id} · привязан` : 'ключ создастся сам'}
                  </div>
                </div>
              </div>
              <span className="label">это устройство</span>
            </div>
          )}
          <button onClick={onConnect}
            className="tap w-full flex items-center justify-center gap-2.5 py-4 display text-[23px]"
            style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)' }}>
            {connectLabel}<Icon name="arrow-right" size={22} />
          </button>
          {active && (
            <div className="num text-[10.5px] text-center px-3 py-2.5 border-t" style={{ color: 'var(--color-muted)', borderColor: 'var(--color-line)' }}>
              откроется <b style={{ color: 'var(--color-ink)' }}>INCY</b> — профиль добавится сам
            </div>
          )}
        </div>

        {/* other devices peek */}
        {others.length > 0 && (
          <button onClick={() => { haptic('light'); setLocation('/devices') }}
            className="tap w-full flex items-center gap-3 mt-2.5 px-3.5 py-3 block">
            <span className="flex">
              {others.slice(0, 2).map((d, i) => (
                <span key={d.id} className="size-[28px] rounded-[7px] flex items-center justify-center"
                  style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-bg)', marginLeft: i ? -8 : 0, color: 'var(--color-muted)' }}>
                  <Icon name={PLAT_ICON[d.platform] ?? 'device-mobile'} size={15} />
                </span>
              ))}
            </span>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-[13.5px] font-semibold">Другие устройства</span>
              <span className="num text-[10px] block truncate" style={{ color: 'var(--color-muted)' }}>{others.map(d => d.label).join(' · ')}</span>
            </span>
            <span className="num text-[10px] tracking-[0.1em] uppercase flex items-center gap-1 shrink-0" style={{ color: 'var(--color-accent)' }}>все <Icon name="chevron-right" size={14} /></span>
          </button>
        )}
        <div className="h-3" />
      </div>
    </motion.div>
  )
}
